'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type {
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
} from 'ag-grid-community';
import { Box } from '@terra/ui-styled-system';
import { TABLE_IDS, Table } from '@terra/ui-design-system';
import { File01Icon } from '@terra/ui-icons';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';
import { EmptyState } from '../../EmptyState';
import {
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
} from '../../common/list-page-table-theme';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import { isRowClickDisabledTarget } from '../rowClickBlocker';
import { useReportsTableColumnDefs } from './useReportsTableColumnDefs';

const REPORTS_TABLE_ROW_HEIGHT_PX = 36;
const DEFAULT_COL_DEF: ColDef<ReportsReturnDto> = {
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['desc', 'asc'],
};

export const REPORTS_TABLE_EMPTY_KIND = {
  NO_TARGET: 'NO_TARGET',
  EMPTY: 'EMPTY',
  FILTERED: 'FILTERED',
} as const;

export type ReportsTableEmptyKind =
  (typeof REPORTS_TABLE_EMPTY_KIND)[keyof typeof REPORTS_TABLE_EMPTY_KIND];

type ReportsTableProps = {
  reports: ReportsReturnDto[];
  selectedReportId: string | null;
  isLoading: boolean;
  emptyKind: ReportsTableEmptyKind;
  onOpenReport: (report: ReportsReturnDto) => void;
  onDeleteReport: (report: ReportsReturnDto) => void;
};

const getReportsTableRowId = (params: GetRowIdParams<ReportsReturnDto>) =>
  params.data.id;

function ReportsTableEmptyState({
  emptyKind,
}: Pick<ReportsTableProps, 'emptyKind'>) {
  const t = useTranslations('proxy.reports.table');

  if (emptyKind === REPORTS_TABLE_EMPTY_KIND.NO_TARGET) {
    return (
      <EmptyState
        icon={File01Icon}
        title={t('noTargetTitle')}
        description={t('noTargetDescription')}
      />
    );
  }

  return emptyKind === REPORTS_TABLE_EMPTY_KIND.FILTERED ? (
    <EmptyState
      icon={File01Icon}
      title={t('emptyFilteredTitle')}
      description={t('emptyFilteredDescription')}
    />
  ) : (
    <EmptyState
      icon={File01Icon}
      title={t('emptyTitle')}
      description={t('emptyDescription')}
    />
  );
}

export function ReportsTable({
  reports,
  selectedReportId,
  isLoading,
  emptyKind,
  onOpenReport,
  onDeleteReport,
}: ReportsTableProps) {
  const columnDefs = useReportsTableColumnDefs({ onDeleteReport });
  const persistence = useListPageTablePersistence(TABLE_IDS.PROXY_REPORTS_LIST);
  const [gridApi, setGridApi] = useState<GridApi<ReportsReturnDto> | null>(
    null,
  );
  const selectedReportIdRef = useRef(selectedReportId);

  useEffect(() => {
    selectedReportIdRef.current = selectedReportId;
    gridApi?.redrawRows();
  }, [gridApi, selectedReportId]);

  const getRowStyle = useCallback(
    (params: RowClassParams<ReportsReturnDto>): RowStyle => ({
      cursor: 'pointer',
      ...(params.data?.id === selectedReportIdRef.current
        ? { backgroundColor: 'var(--colors-bg-neutral-subtle-soft)' }
        : {}),
    }),
    [],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<ReportsReturnDto>) => {
      if (!event.data || isRowClickDisabledTarget(event.event)) {
        return;
      }

      onOpenReport(event.data);
    },
    [onOpenReport],
  );

  return (
    <Box
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      css={LIST_PAGE_TABLE_HEADER_CSS}
    >
      <Table
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={reports}
        getRowId={getReportsTableRowId}
        rowHeight={REPORTS_TABLE_ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        onGridReady={(event: GridReadyEvent<ReportsReturnDto>) =>
          setGridApi(event.api)
        }
        onRowClicked={handleRowClicked}
        getRowStyle={getRowStyle}
        suppressNoRowsOverlay
      />
      {reports.length === 0 && !isLoading ? (
        <ListPageTableBodyLayer>
          <ReportsTableEmptyState emptyKind={emptyKind} />
        </ListPageTableBodyLayer>
      ) : null}
    </Box>
  );
}
